import { FormGroup, Label, Modal, SubmitButton, Tooltip } from "@xola/ui-kit";
import { FormField, handleSuccessfulSubmission, PurchaseItemStatus, requiredString, TextareaField } from "common";
import { FormikProps } from "formik";
import React, { useRef, useState } from "react";
import { CommunicationChannelType, Event, MessageTemplate, Purchase, PurchaseItem, Timeslot } from "types";
import { boolean, object, string } from "yup";
import { useSeller } from "../../../hooks/useAccount";
import { useCollection } from "../../../hooks/useCollection";
import { useInvalidate } from "../../../hooks/useInvalidation";
import { useResource } from "../../../hooks/useResource";
import { MessageForm } from "../../Common/MessageModal/MessageForm";
import { TemplateSelect } from "../../Common/MessageModal/TemplateSelect";
import { SecondaryButton } from "../../Common/SecondaryButton";
import { XolaFormik } from "../../Common/XolaFormik";
import { EventSummaryTile } from "../../Event/EventSummaryTile";
import { ChargeCreditCardCheckbox } from "./ChargeCreditCardCheckbox";
import { purchaseTypeActions, shouldShowDoNotChargeNow } from "./helpers";
import useAcceptOrDeclinePurchaseMutation, {
    AcceptOrDeclinePurchaseValues,
} from "./useAcceptOrDeclinePurchaseMutation";

type FormValues = AcceptOrDeclinePurchaseValues;

export interface AcceptOrDeclinePurchaseModalFormProps {
    purchase: Purchase;
    timeslot?: Timeslot;
    item: PurchaseItem;
    updatedStatus: PurchaseItemStatus.CONFIRMED | PurchaseItemStatus.DECLINED;
    onClose: () => void;
    onSuccess?: (purchaseId: string) => void;
}

export const AcceptOrDeclinePurchaseModalForm = ({
    purchase,
    item,
    onClose,
    timeslot,
    updatedStatus,
    onSuccess,
}: AcceptOrDeclinePurchaseModalFormProps) => {
    const seller = useSeller();
    const invalidate = useInvalidate();
    const formikRef = useRef<FormikProps<any> | null>(null);
    const { data: templates } = useCollection<MessageTemplate>("messageTemplates");
    const { data: event, isLoading: isLoadingEvent } = useResource<Event>(["events", timeslot?.event.id]);

    const enabledChannels = seller.preferences.communicationChannels?.enabledChannels ?? [];
    const isSmsAvailable = enabledChannels.includes(CommunicationChannelType.SMS) && !!purchase.phone;
    const [template, setTemplate] = useState<MessageTemplate | null>(null);

    const showDoNotChargeNow = shouldShowDoNotChargeNow(purchase, item, updatedStatus);

    const isMessageFieldDisabled = !purchase.customerEmail;

    const {
        mutateAsync: processPurchase,
        isLoading: isSaving,
        isSuccess,
    } = useAcceptOrDeclinePurchaseMutation(purchase, item);

    const initialValues: FormValues = {
        status: item.status,
        collectPayment: false,
        email: { subject: "", body: "" },
        sms: { body: "" },
        enableSms: false,
        enableEmail: true,
    };

    const validationSchema = getValidationSchema({
        template,
        status: updatedStatus,
    });

    const handleSubmit = async (values: FormValues) => {
        await processPurchase(values);
        handleSuccessfulSubmission(() => {
            if (purchase.organizer?.id) {
                invalidate("users", purchase.organizer.id);
            }

            onSuccess?.(purchase.id);
            onClose();
        });
    };

    const handleTemplateChange = (template: MessageTemplate) => {
        setTemplate(template);

        formikRef.current!.setFieldValue("email", {
            subject: template?.email?.subject ?? "",
            body: template?.email?.body ?? "",
        });

        formikRef.current!.setFieldValue("sms", {
            body: template?.sms?.body ?? "",
        });

        if (isSmsAvailable) {
            formikRef.current!.setFieldValue("enableSms", !!template?.sms?.body);
        }
    };

    return (
        <XolaFormik
            innerRef={formikRef}
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
        >
            {({ handleSubmit, setFieldValue, values }) => (
                <>
                    <Modal.Body>
                        <fieldset disabled={isSaving}>
                            {!isLoadingEvent && event ? <EventSummaryTile event={event} /> : null}
                        </fieldset>

                        {!isMessageFieldDisabled && templates.length > 0 ? (
                            <FormGroup>
                                <Label>Load Template</Label>
                                <TemplateSelect
                                    className="flex-1"
                                    value={template}
                                    templates={templates}
                                    onChange={handleTemplateChange}
                                />
                            </FormGroup>
                        ) : null}

                        {template ? (
                            <MessageForm
                                template={template}
                                isEmailAvailable={!!purchase.customerEmail}
                                isSmsAvailable={isSmsAvailable}
                                canToggleEmailChannel={updatedStatus !== PurchaseItemStatus.DECLINED}
                            />
                        ) : (
                            <FormField label={purchaseTypeActions[updatedStatus].messageLabel} name="email.body">
                                <Tooltip
                                    content="No contact information available"
                                    disabled={!isMessageFieldDisabled}
                                    placement="auto"
                                >
                                    <TextareaField
                                        className="h-28"
                                        name="email.body"
                                        placeholder="Message"
                                        disabled={isMessageFieldDisabled}
                                        onChange={(e: any) => setFieldValue("email.body", e.target.value)}
                                    />
                                </Tooltip>
                            </FormField>
                        )}

                        {showDoNotChargeNow && (
                            <ChargeCreditCardCheckbox
                                checked={values.collectPayment}
                                onChange={(checked) => setFieldValue("collectPayment", checked)}
                            />
                        )}
                    </Modal.Body>

                    <Modal.Footer>
                        <fieldset className="flex justify-end space-x-4" disabled={isSaving}>
                            <SecondaryButton onClick={onClose}>Cancel</SecondaryButton>

                            <SubmitButton
                                type="submit"
                                color="success"
                                isLoading={isSaving}
                                isSuccess={isSuccess}
                                onClick={() => {
                                    setFieldValue("status", updatedStatus);
                                    handleSubmit();
                                }}
                            >
                                Confirm
                            </SubmitButton>
                        </fieldset>
                    </Modal.Footer>
                </>
            )}
        </XolaFormik>
    );
};

interface GetValidationSchemaParams {
    template: MessageTemplate | null;
    status: PurchaseItemStatus;
}

export const getValidationSchema = ({ template, status }: GetValidationSchemaParams) => {
    return object({
        email: object().when("enableEmail", (enableEmail: boolean) => {
            const shouldValidateEmail = enableEmail && template && !!template?.email;

            return object({
                subject: shouldValidateEmail ? requiredString() : string(),
                body: shouldValidateEmail || status === PurchaseItemStatus.DECLINED ? requiredString() : string(),
            });
        }),

        sms: object().when("enableSms", (enableSms: boolean) => {
            const shouldValidateSms = enableSms ? !template?.sms?.body : false;

            return object({
                body: shouldValidateSms ? requiredString().max(737, "must have less than 737 characters") : string(),
            });
        }),

        collectPayment: boolean(),
    });
};
